// Configure your import map in config/importmap.rb. Read more: https://github.com/rails/importmap-rails
import '@hotwired/turbo-rails'
import '@hotwired/hotwire-native-bridge'
import './turbo/actions'
import './turbo/confirm'
import './turbo/form_reset'
import './turbo/scroll_restoration'
import './turbo/native_history'
import './turbo/native_submit'
import './controllers'
import './elements'
import { Browser } from './utils'

// Sans thème forcé, l'apparence résolue par l'app Android (cf. bridge/theme_controller).
const applyTheme = (theme) => {
  const resolved = theme || document.documentElement.dataset.nativeScheme
  if (resolved) document.documentElement.setAttribute('data-theme', resolved)
  else document.documentElement.removeAttribute('data-theme')
}

const currentTheme = () => document.documentElement.getAttribute('data-theme') || ''

// Turbo ne remplace que le <body> : <html> garderait le thème de la page précédente.
// Les captures du cache portent l'ancien thème — les suivre ferait clignoter la page.
document.addEventListener('turbo:before-render', ({ detail }) => {
  if (detail.isPreview) return

  const theme = detail.newBody?.dataset?.themePreference || ''
  const resolved = theme || document.documentElement.dataset.nativeScheme || ''

  if (resolved !== currentTheme()) window.Turbo?.cache?.clear()

  applyTheme(theme)
})

document.addEventListener('turbo:load', () => applyTheme(document.body.dataset.themePreference))

/**
 * Fix: Turbo disables scroll handling for the rest of the document's life
 * Observed on @hotwired/turbo 8.0.13, still unfixed on Turbo's main branch.
 * @see https://github.com/hotwired/turbo/pull/1545
 *
 * A frame navigation carrying data-turbo-action (the category tabs of the
 * Tendances feed, the profile tabs, the user search) makes Turbo Drive run a
 * visit with willRender: false. PageView#renderPage compares that visit's
 * snapshot against the current <head>, finds no tracked element in it, and so
 * sets `forceReloaded` — a flag Turbo initialises once and never resets.
 * Because willRender is false no reload follows, so the flag simply stays on.
 * Visit#performScroll is guarded by it: from then on Turbo neither restores the
 * scroll position on `restore` visits nor scrolls to top on `advance` ones. The
 * DOM is still restored — only the scrolling is silently dead, until a full
 * page load builds a new PageView. Hence "it worked, then it didn't, and a
 * hard refresh fixes it".
 *
 * Clearing the flag after a completed render is safe: a genuine forced reload
 * replaces the document instead of ever reaching turbo:load.
 *
 * https://github.com/hotwired/turbo/pull/1545 fixes this in Turbo itself, the
 * same way this listener does. It is still open, as are the two reports it
 * closes — https://github.com/hotwired/turbo/issues/1344 (December 2024) and
 * https://github.com/hotwired/turbo/issues/1526. Drop this listener once the
 * fix ships in a Turbo release we depend on.
 */
document.addEventListener('turbo:load', () => {
  const view = window.Turbo?.session?.view
  if (view) view.forceReloaded = false
})

/**
 * Mark turbo intercepted request with custom header for server specific html rendering
 * @see https://turbo.hotwired.dev/handbook/drive#pausing-requests
 */
document.addEventListener('turbo:before-fetch-request', async (event) => {
  event.preventDefault()
  event.detail.fetchOptions.headers['Turbo-Request'] = 'true'
  event.detail.resume()
})

/**
 * Un rendu Turbo qui n'apporte AUCUN changement visible ne doit pas jouer de view
 * transition : les animations des onglets du profil (application.css) se rejoueraient
 * à vide — le label tressaute. Trois rendus « pour rien » existent :
 *   - le rendu frais qui suit un aperçu du cache (deux documents identiques — cas
 *     visible même en arrivant d'une page SANS meta, car le couple aperçu/frais, lui,
 *     la porte des deux côtés) ;
 *   - la re-visite de la page active (re-tap « Mon profil » déjà sur le profil) ;
 *   - en natif, la visite de restauration émise au réaffichage d'un écran d'onglet
 *     (visitableViewWillAppear). Sur le web une restauration est un back/forward, où
 *     la transition est légitime — on ne supprime qu'en natif. turbo:visit et non
 *     before-visit : les restaurations n'émettent pas before-visit.
 *
 * On enrobe startViewTransition plutôt que toucher à la meta : PageSnapshot.clone() ne
 * clone pas le head, le cache de snapshots partage donc les nœuds vifs du head — muter
 * `content` réécrivait le snapshot en cache de la page précédente (sa meta, identique,
 * survit au merge du head), et ses previews suivantes « popaient ».
 */

;(() => {
  let suppressing = false
  let activeTransition = null

  // Pas location.href : l'adaptateur natif change l'historique AVANT turbo:visit,
  // l'URL courante est déjà la cible et toute visite passerait pour une re-visite.
  let renderedURL = window.location.href

  document.addEventListener('turbo:visit', (event) => {
    // L'égalité avec location.href ne suffit pas : sur le web, location-replace réécrit
    // /profil en /profil/:id, alors qu'un re-tap « Mon profil » visite /profil. Un lien
    // déjà marqué aria-current="page" qui pointe vers l'URL visitée est le signal sûr
    // d'une re-visite de la page active. (En natif la réécriture n'a plus lieu — elle
    // scindait l'identité de la page en deux clés de cache, cf. [TURBO-NATIVE-CACHE].)
    const nativeRestore = event.detail.action === 'restore' && Browser.isHotwireNative
    const revisit = event.detail.url === renderedURL ||
      [...document.querySelectorAll('a[aria-current="page"]')].some((a) => a.href === event.detail.url)
    if (nativeRestore || revisit) suppressing = true
  })
  document.addEventListener('turbo:load', () => {
    suppressing = false
    renderedURL = window.location.href
  })
  // La page se cache en pleine transition : on saute à l'état final tout de
  // suite, sinon elle reste gelée jusqu'au réaffichage.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) activeTransition?.skipTransition()
  })

  if (document.startViewTransition) {
    const original = document.startViewTransition.bind(document)
    document.startViewTransition = (callback) => {
      // data-turbo-preview encore présent = ce rendu part d'un aperçu (l'attribut n'est
      // retiré qu'à l'intérieur du rendu frais, après cette décision).
      const fromPreview = document.documentElement.hasAttribute('data-turbo-preview')
      // Le clamp du slide de panneau (data-panel-slide, animations.css) doit
      // tomber dès la fin réelle de la transition : tant qu'il est posé, la page
      // est limitée à un écran et le bas est clippé — un scroll rapide le voyait.
      const clearPanelSlide = () => document.documentElement.removeAttribute('data-panel-slide')

      // Document caché (webview d'un autre onglet natif, écran empilé au-dessus) :
      // WebKit gèle les animations — la transition resterait en vol, ses snapshots
      // recouvrant la page. Rendu sec dans ce cas.
      if (!suppressing && !fromPreview && document.visibilityState === 'visible') {
        const transition = original(callback)
        activeTransition = transition
        transition.finished.finally(() => {
          if (activeTransition === transition) activeTransition = null
          clearPanelSlide()
        })
        return transition
      }

      // Faux ViewTransition : le rendu s'exécute, rien ne s'anime. Turbo n'attend
      // que `finished`.
      const done = Promise.resolve().then(callback).then(() => undefined)
      done.then(clearPanelSlide)
      return { finished: done, ready: done, updateCallbackDone: done, skipTransition: () => {} }
    }
  }
})()

/**
 * Slide du panneau des onglets profil (app native) : pose data-panel-slide
 * (forward/back) sur :root au départ de la visite — les règles existantes
 * d'animations.css (#profile-panel, :root[data-panel-slide]) font le reste.
 */
;(() => {
  if (!Browser.isHotwireNative) return

  const PATHS = ['/profil', '/listes-suivies', '/reservations', '/archives']
  const root = document.documentElement
  // Horloge et courbe partagées avec les tabs (animations.css lit ces variables).
  root.style.setProperty('--tabs-speed', '300ms')
  root.style.setProperty('--tabs-ease', 'cubic-bezier(0.32, 0.72, 0, 1)')

  document.addEventListener('turbo:visit', (event) => {
    root.removeAttribute('data-panel-slide')
    const to = PATHS.indexOf(new URL(event.detail.url).pathname)
    if (to < 0) return

    const from = [...document.querySelectorAll('a[aria-current="page"]')]
      .map((a) => PATHS.indexOf(new URL(a.href).pathname))
      .find((index) => index >= 0)
    if (from === undefined || from === to) return

    root.setAttribute('data-panel-slide', to > from ? 'forward' : 'back')
  })

})()

/**
 * Turbo 8.0.13 ne prefetch qu'au mouseenter (LinkPrefetchObserver) : inerte au
 * tactile. Au touchstart sur un lien marqué data-turbo-prefetch, on synthétise
 * un mouseenter — l'observer part en requête pendant que le doigt est encore
 * posé, la visite du click consomme la réponse du cache de prefetch.
 */
document.addEventListener('touchstart', (event) => {
  const link = event.target.closest?.('[data-turbo-prefetch] a[href], a[href][data-turbo-prefetch]')
  if (link) link.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }))
}, { capture: true, passive: true })


;(function () {
  if (navigator.serviceWorker && Browser.isStandAlone) {
    navigator.serviceWorker.register('/serviceworker.js', { scope: './' })
  }
})()

/**
 * Automatic change of list surprise mode depending on list kind value
 * until user manually changes surprise mode.
 */
;(function () {
  const surpriseListKinds = ['birthday', 'xmas']
  document.addEventListener('turbo:load', (event) => {
    const newListForm = document.getElementById("new_lists_basic")
    if (!newListForm) return

    const listKind = newListForm.querySelector("#lists_basic_kind")
    const listSurpriseMode = newListForm.querySelector("#lists_basic_surprise_mode")
    const onKindChange = (e) => listSurpriseMode.checked = surpriseListKinds.includes(listKind.value)

    listKind.addEventListener('change', onKindChange)
    listSurpriseMode.addEventListener('change', () => {
      listKind.removeEventListener('change', onKindChange)
    }, {once: true})
  })
})()
